import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Scale, FileText, History, Trash2, Download, Printer, Copy, 
  AlertTriangle, Plus, X, Check, ArrowLeft, Command 
} from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'

import { CreatorLinks } from './lib/creatorLinks'
import CareLoadScale from './components/CareLoadScale'
import type { SessionState, CareLoadState, Receipt, StratumKey, FieldResponse } from './lib/types'
import { STRATA_DEFS, getLoadLabel } from './lib/types'
import { synthesizeReading } from './lib/synthesize'

const LINKEDIN = 'https://www.linkedin.com/in/mindful-abhay/'
const BUYMECOFFEE = 'https://buymeacoffee.com/abhaypratapsingh'

const LOCAL_KEY = 'careload_local_v1'

function cn(...c: (string | false | null | undefined)[]) { return c.filter(Boolean).join(' ') }

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    credentials: 'include',
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

function loadLocal(): any {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}') } catch { return {} }
}
function saveLocal(data: any) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(data))
}

const DEFAULT_LOAD: CareLoadState = {
  total: 4.2,
  diffuse: false,
  strata: STRATA_DEFS.map(d => ({ key: d.key, label: d.label, value: 1.8 })),
}

const INITIAL_RESPONSES: Record<string, FieldResponse> = {}

const DEFAULT_STATE: SessionState = {
  id: 'local_' + Date.now().toString(36),
  createdAt: new Date().toISOString(),
  fieldNotes: '',
  load: DEFAULT_LOAD,
  responses: INITIAL_RESPONSES,
  uncertaintyTotal: 0,
}

function makeEmptyState(): SessionState {
  return {
    id: 's_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
    createdAt: new Date().toISOString(),
    fieldNotes: '',
    load: JSON.parse(JSON.stringify(DEFAULT_LOAD)),
    responses: {},
    uncertaintyTotal: 0,
  }
}

function updateResponse(state: SessionState, key: string, value: any, uncertainty: 0 | 1 = 0): SessionState {
  const prev = state.responses[key]
  const nextUnc = ((prev?.uncertainty || 0) + uncertainty > 0 ? 1 : 0) as 0 | 1
  const newResp: FieldResponse = { fieldKey: key, value, uncertainty: nextUnc, ts: new Date().toISOString() }
  const nextResponses = { ...state.responses, [key]: newResp }
  const uncTotal = Object.values(nextResponses).reduce((n, r) => n + (r.uncertainty || 0), 0)
  return { ...state, responses: nextResponses, uncertaintyTotal: uncTotal }
}

function setUncertainty(state: SessionState, key: string, on: boolean): SessionState {
  const r = state.responses[key]
  if (!r) return state
  const next = { ...state.responses, [key]: { ...r, uncertainty: (on ? 1 : 0) as 0 | 1 } }
  const uncTotal = Object.values(next).reduce((n, rr) => n + (rr.uncertainty || 0), 0)
  return { ...state, responses: next, uncertaintyTotal: uncTotal }
}

export default function Careload() {
  const [route, setRoute] = useState<'instrument' | 'receipts' | 'privacy'>('instrument')
  const [userId, setUserId] = useState<string | null>(null)
  const [localOnly, setLocalOnly] = useState(false)

  const [state, setState] = useState<SessionState>(() => {
    const loc = loadLocal()
    return loc.currentState || makeEmptyState()
  })

  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null)
  const [issuing, setIssuing] = useState(false)
  const [showCmd, setShowCmd] = useState(false)

  // Identity bootstrap + data load
  useEffect(() => {
    api<{ userId: string }>('/api/me').then(r => {
      setUserId(r.userId)
      setLocalOnly(false)
      loadMyReceipts()
    }).catch(() => {
      const local = 'local_' + Math.random().toString(36).slice(2, 9)
      setUserId(local)
      setLocalOnly(true)
      const loc = loadLocal()
      if (loc.receipts) setReceipts(loc.receipts)
    })
  }, [])

  useEffect(() => {
    if (localOnly) saveLocal({ currentState: state, receipts, savedAt: new Date().toISOString() })
  }, [state, receipts, localOnly])

  async function loadMyReceipts() {
    try {
      const res = await api<{ receipts: Receipt[] }>('/api/receipts')
      setReceipts(res.receipts || [])
    } catch {
      const loc = loadLocal()
      if (loc.receipts) setReceipts(loc.receipts)
    }
  }

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setShowCmd(v => !v) }
      if (e.key === 'Escape') { setShowCmd(false); if (selectedReceipt) setSelectedReceipt(null) }
      if (e.key.toLowerCase() === 'r' && document.activeElement?.tagName === 'BODY') { setRoute('receipts') }
      if (e.key.toLowerCase() === 'n' && document.activeElement?.tagName === 'BODY') {
        startNewCalibration()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedReceipt])

  // ===== Core mutations (deliberate practice) =====
  const setLoad = useCallback((next: CareLoadState) => {
    setState(s => ({ ...s, load: next }))
    // fire light analytics (best effort)
    logEvent('scale_adjusted', { total: next.total, diffuse: next.diffuse })
  }, [])

  const setField = (key: string, value: any, uncertainty: 0 | 1 = 0) => {
    setState(s => updateResponse(s, key, value, uncertainty))
    logEvent('field_answered', { key, uncertainty })
  }

  const toggleUncertainty = (key: string) => {
    setState(s => {
      const r = s.responses[key]
      const next = setUncertainty(s, key, !r?.uncertainty)
      logEvent('uncertainty_flagged', { key, on: !r?.uncertainty })
      return next
    })
  }

  const setFieldNotes = (notes: string) => setState(s => ({ ...s, fieldNotes: notes }))

  // Situating chips
  const SITUATING_OPTIONS = [
    'Uncertain invitation I have not answered',
    'Request or ask whose weight I am still carrying',
    'Yes I gave that now feels costly',
    'No I need language and timing for',
    'Unclear social debt or expectation',
    'Ambiguous "maybe" that is eroding capacity',
  ]

  // Fragmentation
  const [newLoop, setNewLoop] = useState('')
  const fragLoops: string[] = state.responses['frag_loops']?.value || []
  function addLoop() {
    if (!newLoop.trim()) return
    const next = [...fragLoops, newLoop.trim()].slice(0, 9)
    setField('frag_loops', next)
    setField('frag_count', next.length)
    setNewLoop('')
  }
  function removeLoop(idx: number) {
    const next = fragLoops.filter((_, i) => i !== idx)
    setField('frag_loops', next)
    setField('frag_count', next.length)
  }

  // Readiness
  const SOMATIC = ['throat', 'chest', 'solar plexus', 'shoulders / upper back', 'jaw / temples', 'behind the eyes', 'whole system (diffuse)', 'I do not locate it clearly in the body']
  const READINESS = [
    'Not yet available — body/mind says wait',
    'Available but would cost noticeably',
    'The shape of a clean move is present',
    'I can feel the refusal but not the words yet',
    'Surprisingly the move feels light',
  ]

  // Recovery
  const [newRecovery, setNewRecovery] = useState('')
  const recoveryVectors: string[] = state.responses['recovery_vectors']?.value || []
  function addRecovery() {
    if (!newRecovery.trim()) return
    const next = [...recoveryVectors, newRecovery.trim()].slice(0, 5)
    setField('recovery_vectors', next)
    setNewRecovery('')
  }
  function removeRecovery(i: number) {
    setField('recovery_vectors', recoveryVectors.filter((_, idx) => idx !== i))
  }

  // Clarity (the deliberate practice heart)
  const clarityNote: string = state.responses['clarity_note']?.value || ''

  // Uncertainty helper component
  function UncertaintyToggle({ fieldKey }: { fieldKey: string }) {
    const isUncertain = !!state.responses[fieldKey]?.uncertainty
    return (
      <button
        onClick={() => toggleUncertainty(fieldKey)}
        className={cn("text-[10px] px-2 py-0.5 rounded border ml-2 align-middle", isUncertain ? "border-[#854d0e] text-[#c9a227] bg-[#1c140a]" : "border-[#1f2537] text-[#6b7280]")}
      >
        {isUncertain ? 'UNCERTAIN (tap to clear)' : 'mark uncertain'}
      </button>
    )
  }

  // ===== Receipt issuance =====
  async function issueReceipt() {
    setIssuing(true)
    const reading = synthesizeReading(state)
    const payload = {
      sessionId: state.id,
      careLoad: state.load.total,
      careLoadLabel: getLoadLabel(state.load.total),
      fragmentationCount: (state.responses['frag_loops']?.value || []).length || state.responses['frag_count']?.value || 0,
      uncertaintyCount: state.uncertaintyTotal,
      readingText: reading,
      snapshot: state,
      fieldNotes: state.fieldNotes,
    }

    try {
      if (localOnly) {
        const rec: Receipt = {
          id: 'loc_rcpt_' + Date.now().toString(36),
          sessionId: state.id,
          issuedAt: new Date().toISOString(),
          careLoad: payload.careLoad,
          careLoadLabel: payload.careLoadLabel,
          fragmentationCount: payload.fragmentationCount,
          uncertaintyCount: payload.uncertaintyCount,
          readingText: payload.readingText,
          snapshot: state,
          privateCode: 'local-' + Math.random().toString(36).slice(2, 10),
        }
        const nextReceipts = [rec, ...receipts]
        setReceipts(nextReceipts)
        setSelectedReceipt(rec)
        toast.success('Receipt issued (local only — backend unreachable)')
      } else {
        const res = await api<{ receipt: Receipt }>('/api/receipts', { method: 'POST', body: JSON.stringify(payload) })
        const rec = res.receipt
        setReceipts([rec, ...receipts])
        setSelectedReceipt(rec)
        toast.success('Cognitive receipt issued. Save the code.')
      }
      logEvent('receipt_issued', { load: payload.careLoad, uncertainty: payload.uncertaintyCount })
    } catch (e: any) {
      toast.error('Issue failed: ' + (e.message || 'try again'))
    } finally {
      setIssuing(false)
    }
  }

  function startNewCalibration() {
    const fresh = makeEmptyState()
    setState(fresh)
    setSelectedReceipt(null)
    setRoute('instrument')
    toast('New calibration opened. Previous state is in your receipts.')
  }

  function loadReceiptIntoInstrument(r: Receipt) {
    // Load the snapshot so user can continue or re-issue a new one from this moment
    setState(r.snapshot)
    setSelectedReceipt(null)
    setRoute('instrument')
    toast('Loaded snapshot into the instrument. Adjust and issue a new receipt if you wish.')
  }

  async function deleteEverything() {
    if (!confirm('Delete every session, response, receipt, and trace forever? This cannot be undone.')) return
    try { await api('/api/delete-all', { method: 'POST' }) } catch {}
    setReceipts([])
    setState(makeEmptyState())
    setSelectedReceipt(null)
    localStorage.removeItem(LOCAL_KEY)
    toast('Instrument memory erased.')
    logEvent('delete_all', {})
  }

  async function doExport() {
    try {
      const data = await api('/api/export')
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `careload-export-${format(new Date(), 'yyyy-MM-dd')}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      const blob = new Blob([JSON.stringify({ state, receipts }, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'careload-local-export.json'; a.click(); URL.revokeObjectURL(url)
    }
  }

  function logEvent(type: string, payload: Record<string, any>) {
    // Best effort only — real data lives in D1 responses + receipts
    try {
      if (!localOnly) {
        fetch('/api/events', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type, payload }) }).catch(() => {})
      }
    } catch {}
  }

  // ===== Receipt rendering (print + image) =====
  function renderReceiptPNG(receipt: Receipt) {
    const canvas = document.createElement('canvas')
    canvas.width = 820
    canvas.height = 1100
    const ctx = canvas.getContext('2d', { alpha: true })!
    const p = receipt.snapshot

    ctx.fillStyle = '#f8f5eb'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#1f2520'
    ctx.font = '600 13px Inter, system-ui'
    ctx.fillText('CARE LOAD SCALE  •  COGNITIVE RECEIPT', 48, 52)
    ctx.font = '500 11px Inter, system-ui'
    ctx.fillStyle = '#555'
    ctx.fillText(`${receipt.id}  •  ${format(new Date(receipt.issuedAt), 'dd MMM yyyy HH:mm')}`, 48, 74)

    ctx.fillStyle = '#1f2520'
    ctx.font = '700 72px Georgia, serif'
    ctx.fillText(receipt.careLoad.toFixed(1), 48, 170)

    ctx.font = '500 18px Georgia, serif'
    ctx.fillText(receipt.careLoadLabel, 48, 198)

    let y = 250
    ctx.font = '500 13px Inter, system-ui'
    ctx.fillStyle = '#333'
    ctx.fillText('FRAGMENTS LOGGED', 48, y); y += 22
    ctx.font = '400 14px Georgia, serif'
    const loops = p.responses['frag_loops']?.value || []
    if (loops.length) loops.forEach((l: string) => { ctx.fillText('• ' + l, 56, y); y += 20 })
    else ctx.fillText('(none named)', 56, y)

    y += 18
    ctx.font = '500 13px Inter, system-ui'
    ctx.fillText('RECOVERY VECTORS', 48, y); y += 20
    ctx.font = '400 14px Georgia, serif'
    const recs = p.responses['recovery_vectors']?.value || []
    if (recs.length) recs.forEach((r: string) => { ctx.fillText('• ' + r, 56, y); y += 20 })
    else ctx.fillText('(none recorded)', 56, y)

    y += 18
    ctx.font = '500 13px Inter, system-ui'
    ctx.fillText('SOMATIC & READINESS', 48, y); y += 20
    ctx.font = '400 14px Georgia, serif'
    const som = p.responses['readiness_somatic']?.value || ''
    const ready = p.responses['readiness_phrase']?.value || ''
    if (som) { ctx.fillText(som, 56, y); y += 20 }
    if (ready) { ctx.fillText(ready, 56, y); y += 20 }

    y += 14
    ctx.font = '500 13px Inter, system-ui'
    ctx.fillText('POST-RESET CLARITY NOTE', 48, y); y += 20
    ctx.font = '400 14px Georgia, serif'
    const clar = (p.responses['clarity_note']?.value || '').toString()
    const wrapped = clar.match(/.{1,68}/g) || []
    wrapped.forEach((line: string) => { ctx.fillText(line, 56, y); y += 19 })

    y += 10
    ctx.font = '500 13px Inter, system-ui'
    ctx.fillText('INSTRUMENT READING', 48, y); y += 20
    ctx.font = '400 13.5px Georgia, serif'
    const readingLines = (receipt.readingText || '').match(/.{1,72}/g) || []
    readingLines.forEach((ln: string) => { ctx.fillText(ln, 56, y); y += 18 })

    y += 26
    ctx.font = '400 11px Inter, system-ui'
    ctx.fillStyle = '#555'
    ctx.fillText(`Uncertainty flags: ${receipt.uncertaintyCount}   •   Field notes: ${p.fieldNotes ? 'present' : 'none'}`, 48, y)

    y += 36
    ctx.font = '400 10px Inter, system-ui'
    ctx.fillText('This receipt is a snapshot. You are the sole authority on its meaning.', 48, y)
    ctx.fillText('careload — Abhay Pratap Singh — linkedin.com/in/mindful-abhay  •  buymeacoffee.com/abhaypratapsingh', 48, y + 16)

    const url = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = `careload-receipt-${receipt.id}.png`
    a.click()
  }

  function copyReceiptText(receipt: Receipt) {
    const p = receipt.snapshot
    const text = [
      `CARE LOAD SCALE — COGNITIVE RECEIPT`,
      `${receipt.id} • ${format(new Date(receipt.issuedAt), 'yyyy-MM-dd HH:mm')}`,
      ``,
      `Care load: ${receipt.careLoad.toFixed(1)} — ${receipt.careLoadLabel}`,
      ``,
      `Fragments:`,
      (p.responses['frag_loops']?.value || []).map((l: string) => '• ' + l).join('\n') || '(none)',
      ``,
      `Recovery vectors:`,
      (p.responses['recovery_vectors']?.value || []).map((r: string) => '• ' + r).join('\n') || '(none)',
      ``,
      `Somatic: ${p.responses['readiness_somatic']?.value || ''}`,
      `Readiness: ${p.responses['readiness_phrase']?.value || ''}`,
      ``,
      `Clarity note:`,
      p.responses['clarity_note']?.value || '(none)',
      ``,
      `Instrument reading:`,
      receipt.readingText,
      ``,
      `Uncertainty flags: ${receipt.uncertaintyCount}`,
      `Field notes: ${p.fieldNotes || '(none)'}`,
      ``,
      `This receipt is a snapshot. You are the sole authority on its meaning.`,
      `careload — Abhay Pratap Singh`,
      `https://www.linkedin.com/in/mindful-abhay/  •  https://buymeacoffee.com/abhaypratapsingh`,
    ].join('\n')
    navigator.clipboard.writeText(text)
    toast.success('Receipt text copied')
  }

  // ===== UI =====
  const currentFragCount = (state.responses['frag_loops']?.value || []).length || state.responses['frag_count']?.value || 0
  const liveReading = synthesizeReading(state)

  return (
    <div className="min-h-screen bg-[#05070f] text-[#e2e8f0] pb-20">
      {/* Sticky instrument header */}
      <div className="border-b border-[#1f2537] sticky top-0 z-50 bg-[#05070f]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div onClick={() => { setRoute('instrument'); setSelectedReceipt(null) }} className="flex items-center gap-2 cursor-pointer">
              <div className="w-6 h-6 rounded border border-[#c9a227] flex items-center justify-center">
                <Scale size={14} className="text-[#c9a227]" />
              </div>
              <div className="font-semibold tracking-tight text-lg">careload</div>
            </div>
            <div className="text-[10px] px-2 py-px rounded bg-[#1f2537] text-[#6b7280] tracking-[1.5px] mt-0.5">COGNITIVE INSTRUMENT</div>
          </div>

          <div className="flex items-center gap-1.5 text-sm">
            <button onClick={() => setRoute('instrument')} className={cn("nav-link px-3 py-1", route === 'instrument' && 'active')}>Instrument</button>
            <button onClick={() => { setRoute('receipts'); loadMyReceipts() }} className={cn("nav-link px-3 py-1", route === 'receipts' && 'active')}>Receipts</button>
            <button onClick={() => setRoute('privacy')} className={cn("nav-link px-3 py-1", route === 'privacy' && 'active')}>Privacy</button>
            <button onClick={startNewCalibration} className="btn-ghost px-3 py-1 text-xs flex items-center gap-1 ml-1"><Plus size={14}/>New</button>
            <button onClick={() => setShowCmd(true)} className="btn-ghost px-2 py-1 text-xs"><Command size={14}/></button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-8">
        {/* Hero */}
        {route === 'instrument' && (
          <div className="mb-7">
            <div className="font-serif text-[56px] leading-[0.92] tracking-[-1.8px] mb-2">A scale for what<br />you have not yet said.</div>
            <div className="max-w-2xl text-xl text-[#a1a7c0]">
              For neurodiverse users holding social ambiguity — invitations, refusals, unclear asks. 
              No advice. No correctness. Only precise recording of attention, load, readiness, and the clarity that appears after.
            </div>
            <div className="mt-4"><CreatorLinks /></div>
          </div>
        )}

        {/* INSTRUMENT — the main experience */}
        {route === 'instrument' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left / main column */}
            <div className="lg:col-span-7 space-y-6">
              {/* Live scale — the visual frame */}
              <CareLoadScale load={state.load} onChange={setLoad} />

              {/* 1. Situating */}
              <div className="card p-5">
                <div className="flex items-center justify-between">
                  <div className="section-title">Station 1 — Situating the ambiguity</div>
                  <UncertaintyToggle fieldKey="situating_shape" />
                </div>
                <div className="text-sm text-[#a1a7c0] mt-1">Choose the closest shape. Or write your own. You can be imprecise.</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SITUATING_OPTIONS.map(opt => (
                    <button key={opt} onClick={() => setField('situating_shape', opt)}
                      className={cn("text-sm px-3 py-2 rounded-xl border transition", state.responses['situating_shape']?.value === opt ? "border-[#c9a227] bg-[#1a160a]" : "border-[#1f2537] hover:border-[#3a4258]")}>
                      {opt}
                    </button>
                  ))}
                </div>
                <textarea className="textarea mt-3 text-sm" placeholder="Or describe the shape in your own words (optional)..."
                  value={state.responses['situating_shape_free']?.value || ''} onChange={e => setField('situating_shape_free', e.target.value)} />
              </div>

              {/* 2. Attention fragmentation */}
              <div className="card p-5">
                <div className="flex items-center justify-between">
                  <div className="section-title">Station 2 — Attention fragmentation</div>
                  <UncertaintyToggle fieldKey="frag_loops" />
                </div>
                <div className="text-sm text-[#a1a7c0] mt-1">Name the distinct internal tracks currently running. Add up to 9.</div>

                <div className="mt-3 flex gap-2">
                  <input className="input flex-1 text-sm" placeholder="e.g. “what they will think of me” or “my actual energy on Tuesday”"
                    value={newLoop} onChange={e => setNewLoop(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addLoop() }} />
                  <button onClick={addLoop} className="btn-ghost">Add track</button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {fragLoops.length === 0 && <div className="text-xs text-[#6b7280]">No tracks named yet. You can also mark the whole station uncertain.</div>}
                  {fragLoops.map((loop, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-[#11141f] border border-[#1f2537] rounded-full pl-3 pr-1 py-1 text-sm">
                      {loop}
                      <button onClick={() => removeLoop(i)} className="text-[#6b7280] hover:text-[#c17a3f] px-1"><X size={14}/></button>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-[#6b7280] mt-2">{currentFragCount} track{currentFragCount === 1 ? '' : 's'} logged</div>
              </div>

              {/* 3. Readiness + somatic (combined for flow) */}
              <div className="card p-5 space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="section-title">Station 3 — Readiness state</div>
                    <UncertaintyToggle fieldKey="readiness_somatic" />
                  </div>
                  <div className="text-sm text-[#a1a7c0] mt-1">Where the ambiguity lives in the body right now, if anywhere.</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {SOMATIC.map(s => (
                      <button key={s} onClick={() => setField('readiness_somatic', s)}
                        className={cn("text-sm px-3 py-1.5 rounded-xl border", state.responses['readiness_somatic']?.value === s ? "border-[#c9a227]" : "border-[#1f2537]")}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <div className="section-title">Readiness for a clean move (yes-with-boundaries or no)</div>
                    <UncertaintyToggle fieldKey="readiness_phrase" />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {READINESS.map(r => (
                      <button key={r} onClick={() => setField('readiness_phrase', r)}
                        className={cn("text-sm px-3 py-1.5 rounded-xl border", state.responses['readiness_phrase']?.value === r ? "border-[#c9a227]" : "border-[#1f2537]")}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 4. Recovery need */}
              <div className="card p-5">
                <div className="flex items-center justify-between">
                  <div className="section-title">Station 4 — Recovery need</div>
                  <UncertaintyToggle fieldKey="recovery_vectors" />
                </div>
                <div className="text-sm text-[#a1a7c0] mt-1">The smallest concrete restoration that would meaningfully change the load right now. Your words only.</div>
                <div className="mt-3 flex gap-2">
                  <input className="input flex-1 text-sm" placeholder="e.g. “20 minutes of no decisions” or “saying the truth out loud to one safe person first”"
                    value={newRecovery} onChange={e => setNewRecovery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addRecovery() }} />
                  <button onClick={addRecovery} className="btn-ghost">Add</button>
                </div>
                <div className="mt-2 space-y-1.5">
                  {recoveryVectors.length === 0 && <div className="text-xs text-[#6b7280]">No vectors named.</div>}
                  {recoveryVectors.map((v, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#11141f] border border-[#1f2537] rounded-xl px-3 py-2 text-sm">
                      {v}
                      <button onClick={() => removeRecovery(i)} className="text-[#6b7280] hover:text-red-400"><X size={15}/></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5. Post-reset clarity — the deliberate practice payoff */}
              <div className="card p-5">
                <div className="flex items-center justify-between">
                  <div className="section-title">Station 5 — Post-reset clarity</div>
                  <UncertaintyToggle fieldKey="clarity_note" />
                </div>
                <div className="text-sm text-[#a1a7c0] mt-1">If the ambiguity were cleanly resolved in a way that honored your actual capacity, what small truth or capacity becomes audible again?</div>
                <textarea className="textarea mt-3 text-[15px]" placeholder="Write the quiet thing that appears on the other side of this..."
                  value={clarityNote} onChange={e => setField('clarity_note', e.target.value)} />
              </div>

              {/* Always-available field notes */}
              <div className="card p-5">
                <div className="section-title flex items-center gap-2"><FileText size={14}/> Field notes (margin of the instrument)</div>
                <textarea className="textarea mt-2" placeholder="Anything else that belongs in the record but does not fit the stations. Private to this session."
                  value={state.fieldNotes} onChange={e => setFieldNotes(e.target.value)} />
              </div>
            </div>

            {/* Right column — live receipt preview + actions */}
            <div className="lg:col-span-5 space-y-4">
              <div className="sticky top-16">
                <div className="panel p-4">
                  <div className="section-title mb-2">Live cognitive receipt preview</div>
                  <div className="text-xs text-[#6b7280] mb-3">Updates as you work. Nothing is stored until you issue a receipt.</div>

                  <div className="bg-[#11141f] border border-[#1f2537] rounded-2xl p-4 text-sm font-serif">
                    <div className="uppercase tracking-[2px] text-[10px] text-[#6b7280]">CARE LOAD SCALE — SNAPSHOT</div>
                    <div className="mt-1 text-4xl font-semibold tabular-nums" style={{ color: getLoadLabel(state.load.total).includes('heavy') || state.load.total > 6.5 ? '#c17a3f' : '#c9a227' }}>
                      {state.load.total.toFixed(1)}
                    </div>
                    <div className="text-[#6b7280] -mt-1">{getLoadLabel(state.load.total)}</div>

                    <div className="mt-3 text-xs text-[#6b7280]">FRAGMENTS</div>
                    <div className="text-[#e2e8f0]">{currentFragCount} named tracks</div>

                    <div className="mt-2 text-xs text-[#6b7280]">RECOVERY VECTORS</div>
                    <div className="text-[#e2e8f0]">{recoveryVectors.length ? recoveryVectors[0] : '—'}</div>

                    <div className="mt-2 text-xs text-[#6b7280]">CLARITY NOTE</div>
                    <div className="text-[#e2e8f0] line-clamp-3">{clarityNote || '—'}</div>

                    {state.uncertaintyTotal > 0 && (
                      <div className="mt-2 text-[10px] text-[#c9a227]">{state.uncertaintyTotal} uncertainty flag{state.uncertaintyTotal > 1 ? 's' : ''}</div>
                    )}
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button onClick={issueReceipt} disabled={issuing} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60">
                      <FileText size={16}/> Issue personal cognitive receipt
                    </button>
                    <button onClick={startNewCalibration} className="btn-ghost px-4">Clear</button>
                  </div>
                  <div className="text-[10px] text-[#6b7280] mt-2">Issuing freezes a snapshot and gives you a private receipt you can return to.</div>
                </div>

                {/* Privacy note inline */}
                <div className="mt-3 text-[11px] text-[#6b7280]">
                  Nothing leaves this device until you issue a receipt. You can delete everything later. 
                  <button onClick={() => setRoute('privacy')} className="underline ml-1">Read the full contract</button>.
                </div>

                <div className="mt-4"><CreatorLinks variant="compact" /></div>
              </div>
            </div>
          </div>
        )}

        {/* RECEIPTS / HISTORY */}
        {route === 'receipts' && (
          <div>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="section-title">Personal cognitive receipts</div>
                <div className="text-2xl tracking-tight">Frozen moments from the instrument.</div>
              </div>
              <button onClick={startNewCalibration} className="btn-primary px-4 py-2 text-sm rounded-2xl flex items-center gap-2"><Plus size={15}/> New calibration</button>
            </div>

            {receipts.length === 0 && (
              <div className="empty-state p-8">No receipts yet. Work the instrument and issue one.</div>
            )}

            <div className="grid gap-3">
              {receipts.map(r => (
                <div key={r.id} className="card p-4 flex items-start justify-between gap-4 hover:border-[#3a4258] cursor-pointer" onClick={() => setSelectedReceipt(r)}>
                  <div className="min-w-0">
                    <div className="font-medium tabular-nums text-xl">{r.careLoad.toFixed(1)} <span className="text-base font-normal text-[#6b7280]">— {r.careLoadLabel}</span></div>
                    <div className="text-sm text-[#a1a7c0] mt-0.5 line-clamp-1">{(r.snapshot.responses['clarity_note']?.value || '').toString().slice(0, 110) || 'No clarity note logged.'}</div>
                    <div className="text-[11px] text-[#6b7280] mt-1">{format(new Date(r.issuedAt), 'dd MMM yyyy • HH:mm')} • {r.fragmentationCount} fragments • {r.uncertaintyCount} uncertain</div>
                  </div>
                  <div className="text-right text-xs text-[#6b7280] shrink-0">View receipt<br />and snapshot</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-3">
              <button onClick={doExport} className="btn-ghost flex items-center gap-2"><Download size={15}/> Export all my data</button>
              <button onClick={deleteEverything} className="btn-ghost flex items-center gap-2 text-[#b45353] border-[#3f2a2a] hover:border-[#b45353]"><Trash2 size={15}/> Delete everything</button>
            </div>
          </div>
        )}

        {/* PRIVACY */}
        {route === 'privacy' && (
          <div className="max-w-3xl prose prose-invert">
            <div className="section-title">Privacy &amp; contract</div>
            <h2 className="font-serif text-3xl tracking-tight mt-2">Nothing is stored until you choose to issue a receipt.</h2>
            <div className="mt-4 text-[#a1a7c0] space-y-4 text-[15px]">
              <p>This instrument exists for one purpose: to let neurodiverse people record, with precision and without judgment, the internal texture of social ambiguity around invitations and refusals.</p>
              <p><strong>What is captured:</strong> The exact state of the Care Load Scale you set, the fragments you named, the recovery vectors you listed in your own words, somatic and readiness signals, post-reset clarity notes, and any uncertainty flags you chose to attach. Field notes you write. Timestamps.</p>
              <p><strong>What is never asked:</strong> Your name, email, the names of other people, the specific events, or any PII. You can be as vague or precise as you need.</p>
              <p><strong>Identity:</strong> Device-backed and anonymous by default (a random ID stored in a cookie and D1). You control access via the private codes shown when you issue receipts. There is no account system and no password recovery.</p>
              <p><strong>Storage &amp; deletion:</strong> Data lives in Cloudflare D1 (and KV for fast lookup). When you issue a receipt the snapshot is frozen. You can delete everything associated with your identity at any time. Local fallback keeps the instrument usable even if the network is unreachable.</p>
              <p><strong>Analytics Engine:</strong> Only coarse, one-way events (scale moved, field answered, receipt issued, uncertainty flagged). No free-text content, no behavioral fingerprinting. Used only to understand whether the instrument is working as designed.</p>
              <p><strong>AI (optional &amp; narrow):</strong> If Workers AI is bound, it may be asked for one short additional mirror sentence on receipt issuance only. The prompt forbids diagnosis, advice, emotion claims, and authority. Deterministic excellent readings are always available and shown first.</p>
              <p><strong>Letting you be wrong safely:</strong> There are no correct answers, no scores, no “people-pleasing index”, and no suggestions that you “should” refuse or accept. Uncertainty is a first-class signal. The receipt always returns authority to you.</p>
            </div>

            <div className="mt-6 p-4 border border-[#1f2537] rounded-2xl text-sm">
              <div className="font-medium mb-1">Your rights</div>
              <ul className="list-disc pl-5 text-[#a1a7c0] space-y-1">
                <li>Export everything you have ever logged here.</li>
                <li>Delete everything instantly.</li>
                <li>Never be nudged, reminded, or scored.</li>
                <li>Use the instrument while entirely offline (localStorage).</li>
              </ul>
            </div>

            <div className="mt-8"><CreatorLinks variant="footer" /></div>
            <div className="text-[11px] text-[#6b7280] mt-2">Last updated for this deployment. The source code contains the canonical version of this contract.</div>
          </div>
        )}
      </div>

      {/* Receipt modal / full view */}
      <AnimatePresence>
        {selectedReceipt && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={() => setSelectedReceipt(null)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} 
              className="receipt-paper w-full max-w-[720px] rounded-2xl p-8 md:p-10" onClick={e => e.stopPropagation()}>
              <div className="receipt-header text-[#555] flex items-center justify-between">
                <div>CARE LOAD SCALE — PERSONAL COGNITIVE RECEIPT</div>
                <div className="mono text-[10px]">{selectedReceipt.id}</div>
              </div>

              <div className="mt-2 flex items-baseline gap-4">
                <div className="text-[92px] leading-none font-semibold tabular-nums tracking-[-4.5px] text-[#1f2520]">{selectedReceipt.careLoad.toFixed(1)}</div>
                <div>
                  <div className="text-xl text-[#1f2520]">{selectedReceipt.careLoadLabel}</div>
                  <div className="text-xs text-[#555]">{format(new Date(selectedReceipt.issuedAt), 'dd MMM yyyy • HH:mm')}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 text-[15px] text-[#222]">
                <div>
                  <div className="uppercase tracking-widest text-[10px] text-[#555] mb-1">Fragments logged</div>
                  {(selectedReceipt.snapshot.responses['frag_loops']?.value || []).length > 0 ? (
                    <ul className="space-y-1">
                      {(selectedReceipt.snapshot.responses['frag_loops']?.value || []).map((l: string, i: number) => <li key={i}>• {l}</li>)}
                    </ul>
                  ) : <div className="text-[#555]">(none named)</div>}
                </div>

                <div>
                  <div className="uppercase tracking-widest text-[10px] text-[#555] mb-1">Recovery vectors</div>
                  {(selectedReceipt.snapshot.responses['recovery_vectors']?.value || []).length > 0 ? (
                    <ul className="space-y-1">
                      {(selectedReceipt.snapshot.responses['recovery_vectors']?.value || []).map((v: string, i: number) => <li key={i}>• {v}</li>)}
                    </ul>
                  ) : <div className="text-[#555]">(none recorded)</div>}
                </div>

                <div>
                  <div className="uppercase tracking-widest text-[10px] text-[#555] mb-1">Somatic signal</div>
                  <div>{selectedReceipt.snapshot.responses['readiness_somatic']?.value || '—'}</div>
                  <div className="mt-1 text-[#555]">{selectedReceipt.snapshot.responses['readiness_phrase']?.value || ''}</div>
                </div>

                <div>
                  <div className="uppercase tracking-widest text-[10px] text-[#555] mb-1">Post-reset clarity</div>
                  <div className="whitespace-pre-wrap">{selectedReceipt.snapshot.responses['clarity_note']?.value || '—'}</div>
                </div>
              </div>

              <div className="mt-6 border-t border-[#d4c9a8] pt-5">
                <div className="uppercase tracking-widest text-[10px] text-[#555] mb-1">Instrument reading</div>
                <div className="font-serif text-[15px] leading-snug text-[#1f2520]">{selectedReceipt.readingText}</div>
              </div>

              {selectedReceipt.snapshot.fieldNotes && (
                <div className="mt-5 text-sm text-[#444]">
                  <div className="uppercase tracking-widest text-[10px] text-[#555] mb-1">Field notes</div>
                  {selectedReceipt.snapshot.fieldNotes}
                </div>
              )}

              <div className="mt-6 text-[11px] text-[#555] flex items-center gap-2">
                Uncertainty flags recorded: {selectedReceipt.uncertaintyCount}
                {selectedReceipt.snapshot.load.diffuse && ' • diffuse load'}
              </div>

              <div className="mt-6 text-[11px] text-[#555] border-t border-[#d4c9a8] pt-4">
                This receipt is a snapshot of one moment. You are the sole authority on its meaning. Nothing here is advice.
              </div>

              <div className="mt-3"><CreatorLinks variant="receipt" /></div>

              <div className="mt-6 flex flex-wrap gap-2 no-print">
                <button onClick={() => renderReceiptPNG(selectedReceipt)} className="btn-ghost flex items-center gap-2"><Download size={15}/> Download PNG</button>
                <button onClick={() => copyReceiptText(selectedReceipt)} className="btn-ghost flex items-center gap-2"><Copy size={15}/> Copy text</button>
                <button onClick={() => window.print()} className="btn-ghost flex items-center gap-2"><Printer size={15}/> Print / save PDF</button>
                <button onClick={() => loadReceiptIntoInstrument(selectedReceipt)} className="btn-ghost flex items-center gap-2"><ArrowLeft size={15}/> Load snapshot into instrument</button>
                <button onClick={() => setSelectedReceipt(null)} className="btn-ghost flex items-center gap-2 ml-auto"><X size={15}/> Close</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Command palette hint */}
      <AnimatePresence>
        {showCmd && (
          <div className="fixed inset-0 z-[80] flex items-start justify-center pt-24 bg-black/60" onClick={() => setShowCmd(false)}>
            <div className="card p-4 w-full max-w-sm text-sm" onClick={e => e.stopPropagation()}>
              <div className="text-xs text-[#6b7280] mb-2">KEYBOARD</div>
              <div className="space-y-1 text-[#e2e8f0]">
                <div><span className="font-mono text-[#c9a227]">N</span> — new calibration</div>
                <div><span className="font-mono text-[#c9a227]">R</span> — view receipts</div>
                <div><span className="font-mono text-[#c9a227]">⌘K</span> / <span className="font-mono text-[#c9a227]">Ctrl K</span> — close this</div>
                <div><span className="font-mono text-[#c9a227]">Esc</span> — close modals</div>
              </div>
              <div className="text-[11px] text-[#6b7280] mt-3">The instrument has no timers and no required fields. Leave whenever you need.</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-5 mt-16 text-[11px] text-[#6b7280]">
        <div className="flex flex-wrap items-center justify-between gap-y-2 border-t border-[#1f2537] pt-4">
          <CreatorLinks variant="footer" />
          <div>No resource library. No advice. Only what you log and the scale you set.</div>
        </div>
      </div>
    </div>
  )
}
